import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BodyM, BodyS, LabelL } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { Dialog } from '@sorare/core/src/components/dialog';
import { glossary } from '@sorare/core/src/lib/glossary';

import { SeasonalityLabel } from '#src/components/lineup/SeasonalityLabel/index.tsx';
import { useUpdateManagerTeam } from '#src/hooks/so5/useUpdateManagerTeam.ts';

import type {
  HideTeamDialog_managerTeam,
  HideTeamDialog_so5Leaderboard,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  padding: var(--double-unit);
  text-align: center;
`;
const Team = styled(Vertical)`
  padding: var(--intermediate-unit);
`;
const Footer = styled(Horizontal)`
  width: 100%;
  padding: var(--double-unit);
`;
type Props = {
  onClose: () => void;
  managerTeam: HideTeamDialog_managerTeam;
  so5Leaderboard: HideTeamDialog_so5Leaderboard;
};
export const HideTeamDialog = ({
  onClose,
  managerTeam,
  so5Leaderboard,
}: Props) => {
  const [hidding, setHiding] = useState(false);
  const { updateManagerTeam } = useUpdateManagerTeam();

  return (
    <Dialog
      open
      maxWidth="xs"
      hideHeader
      onClose={onClose}
      footer={
        <Footer gap={1}>
          <Button onClick={onClose} size="medium" color="quaternary" fullWidth>
            <FormattedMessage {...glossary.cancel} />
          </Button>
          <LoadingButton
            loading={hidding}
            onClick={async () => {
              setHiding(true);
              await updateManagerTeam(
                {
                  managerTeamId: managerTeam.id,
                  hidden: true,
                },
                so5Leaderboard.so5Fixture.slug
              );
              setHiding(false);
              onClose();
            }}
            size="medium"
            color="secondary"
            fullWidth
          >
            <FormattedMessage
              id="HideTeamDialog.hide"
              defaultMessage="Hide team"
            />
          </LoadingButton>
        </Footer>
      }
    >
      <Root gap={3}>
        <Vertical gap={1.5}>
          <BodyL as="h2" bold>
            <FormattedMessage
              id="HideTeamDialog.title"
              defaultMessage="You are about to hide"
            />
          </BodyL>
          <Team center gap={1.5}>
            <img
              src={managerTeam.activeDivisionIconUrl}
              alt=""
              width="40px"
              height="40px"
            />
            <div>
              <BodyM as="p">{managerTeam.name}</BodyM>
              <BodyS as="p">
                <SeasonalityLabel
                  seasonality={managerTeam.seasonality}
                  label={managerTeam.seasonalityName}
                />
              </BodyS>
            </div>
          </Team>
          <LabelL as="p">
            <FormattedMessage
              id="HideTeamDialog.description"
              defaultMessage="You will be able to retrieve this team by adding a team in <strong>{name}</strong>"
              values={{
                name: managerTeam.so5TournamentType.displayName,
                strong: Bold,
              }}
            />
          </LabelL>
        </Vertical>
      </Root>
    </Dialog>
  );
};

HideTeamDialog.fragments = {
  managerTeam: gql`
    fragment HideTeamDialog_managerTeam on ManagerTeam {
      id
      name
      activeDivisionIconUrl
      seasonality
      seasonalityName
      so5TournamentType {
        id
        displayName
      }
    }
  ` as TypedDocumentNode<HideTeamDialog_managerTeam>,
  so5Leaderboard: gql`
    fragment HideTeamDialog_so5Leaderboard on So5Leaderboard {
      slug
      so5Fixture {
        slug
      }
    }
  ` as TypedDocumentNode<HideTeamDialog_so5Leaderboard>,
};
